import { useCallback, useEffect, useState } from 'react';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db } from '../utils/init-firebase';

/**
 * Per-user preference controlling which owner the CRM Dashboard and My Deals
 * pages open on. Only meaningful for CRM Admins / SuperAdmins, since everyone
 * else is always scoped to their own records.
 *
 * Stored at users/{uid}.crmViewPreferences.defaultOwnerScope
 */
export const CRM_OWNER_SCOPE = { SELF: 'self', ALL: 'all' };
export const DEFAULT_CRM_OWNER_SCOPE = CRM_OWNER_SCOPE.SELF;
export const CRM_OWNER_SCOPE_FIELD_PATH = 'crmViewPreferences.defaultOwnerScope';

// /crm/dashboard and /crm/contacts/:contactId are separate <Route> entries, so
// navigating between them remounts the page. Caching the resolved scope keeps
// that remount from re-blocking on Firestore.
const sessionScopeCache = new Map();

function normalizeScope(value) {
  return value === CRM_OWNER_SCOPE.ALL ? CRM_OWNER_SCOPE.ALL : DEFAULT_CRM_OWNER_SCOPE;
}

function buildState(userId) {
  if (!userId) return { userId: null, scope: DEFAULT_CRM_OWNER_SCOPE, loading: false };
  const cached = sessionScopeCache.get(userId);
  return {
    userId,
    scope: cached === undefined ? DEFAULT_CRM_OWNER_SCOPE : cached,
    loading: cached === undefined,
  };
}

/**
 * Reads and writes the default owner scope for a user.
 *
 * @param {string|null} userId - Pass a falsy value to opt out entirely; the hook
 *   then resolves immediately with the default and never touches Firestore.
 */
export function useCrmDefaultOwnerScope(userId) {
  const [state, setState] = useState(() => buildState(userId));

  useEffect(() => {
    if (!userId) {
      setState(buildState(null));
      return undefined;
    }
    setState((prev) => (prev.userId === userId ? prev : buildState(userId)));

    const unsubscribe = onSnapshot(
      doc(db, 'users', userId),
      (docSnap) => {
        const scope = normalizeScope(docSnap.data()?.crmViewPreferences?.defaultOwnerScope);
        sessionScopeCache.set(userId, scope);
        setState({ userId, scope, loading: false });
      },
      (error) => {
        console.error('Error loading CRM default owner scope:', error);
        setState({ userId, scope: DEFAULT_CRM_OWNER_SCOPE, loading: false });
      }
    );

    return () => unsubscribe();
  }, [userId]);

  const updateDefaultOwnerScope = useCallback(
    async (nextScope) => {
      if (!userId) return;
      const scope = normalizeScope(nextScope);
      await updateDoc(doc(db, 'users', userId), { [CRM_OWNER_SCOPE_FIELD_PATH]: scope });
      sessionScopeCache.set(userId, scope);
    },
    [userId]
  );

  return {
    defaultOwnerScope: state.scope,
    loading: state.loading,
    updateDefaultOwnerScope,
  };
}

export default useCrmDefaultOwnerScope;
